import React, { useEffect, useRef, useState } from 'react';
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions,
  DialogContent, DialogTitle, List, ListItem, TextField, Typography,
} from '@mui/material';
import axios from '../../utils/axios';

export default function PrivateBotAccessDialog({ open, bot, onClose }) {
  const [allowedUsers, setAllowedUsers] = useState([]);
  const [username, setUsername] = useState('');
  const [bulkText, setBulkText] = useState('');
  const [copying, setCopying] = useState(false);
  const [copySuccess, setCopySuccess] = useState(false);
  const [manualCopy, setManualCopy] = useState(null);
  const manualCopyRef = useRef(null);
  const [loading, setLoading] = useState(true);
  const [loaded, setLoaded] = useState(false);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [reload, setReload] = useState(0);
  const pendingRef = useRef(false);
  const sessionRef = useRef(0);
  const botId = bot?._id;
  const endpoint = `/telegram-bots/${botId}/access-users`;
  const busy = saving || copying;
  const exportText = allowedUsers.filter(user => user.username).map(user => `@${user.username}`).join('\n');
  const exportDisabled = loading || busy || !loaded || !exportText;

  useEffect(() => {
    setBulkText('');
  }, [open, botId]);

  useEffect(() => {
    if (manualCopy && manualCopyRef.current) {
      manualCopyRef.current.focus();
      manualCopyRef.current.select();
    }
  }, [manualCopy]);

  useEffect(() => {
    const session = ++sessionRef.current;
    pendingRef.current = false;
    setSaving(false);
    setAllowedUsers([]);
    setUsername('');
    setCopying(false);
    setCopySuccess(false);
    setManualCopy(null);
    setLoaded(false);
    setError('');
    setLoading(true);
    if (open && botId) {
      axios.get(endpoint).then(({ data }) => {
        if (session !== sessionRef.current) return;
        setAllowedUsers(data.allowedUsers);
        setLoaded(true);
      }).catch(err => {
        if (session !== sessionRef.current) return;
        setError(err.response?.data?.message || 'Ошибка загрузки списка доступа');
      }).finally(() => {
        if (session === sessionRef.current) setLoading(false);
      });
    }
    return () => { sessionRef.current += 1; };
  }, [open, botId, endpoint, reload]);

  const updateAccess = async (request, clearInput) => {
    if (pendingRef.current || loading || !loaded) return;
    pendingRef.current = true;
    setSaving(true);
    setError('');
    setCopySuccess(false);
    setManualCopy(null);
    const session = sessionRef.current;
    try {
      const { data } = await request();
      if (session !== sessionRef.current) return;
      setAllowedUsers(data.allowedUsers);
      if (clearInput === 'single') setUsername('');
      if (clearInput === 'bulk') setBulkText('');
    } catch (err) {
      if (session !== sessionRef.current) return;
      setError(err.response?.data?.message || 'Ошибка изменения списка доступа');
    } finally {
      if (session === sessionRef.current) {
        pendingRef.current = false;
        setSaving(false);
      }
    }
  };

  const handleAdd = (event) => {
    event.preventDefault();
    if (pendingRef.current || loading || !loaded) return;
    const normalized = username.trim().replace(/^@/, '').toLowerCase();
    if (!/^[a-z0-9_]{1,32}$/.test(normalized)) {
      setError('Введите корректный username: 1–32 латинские буквы, цифры или _, с необязательным @ в начале.');
      return;
    }
    if (allowedUsers.some(user => user.username?.toLowerCase() === normalized)) {
      setError('Этот username уже есть в списке доступа');
      return;
    }
    updateAccess(() => axios.post(endpoint, { username: normalized }), 'single');
  };

  const handleBulkAdd = (event) => {
    event.preventDefault();
    if (pendingRef.current || loading || !loaded) return;
    const lines = bulkText.split(/\r?\n/)
      .map((line, index) => ({ value: line.trim(), number: index + 1 }))
      .filter(line => line.value);
    if (lines.length === 0) {
      setError('Введите хотя бы один username в список.');
      return;
    }
    if (lines.length > 1000) {
      setError('За один раз можно добавить не более 1000 непустых строк.');
      return;
    }
    const normalized = lines.map(line => ({ ...line, value: line.value.replace(/^@/, '').toLowerCase() }));
    const invalidLines = normalized.filter(line => !/^[a-z0-9_]{1,32}$/.test(line.value));
    if (invalidLines.length > 0) {
      setError(`Некорректный username в строках: ${invalidLines.map(line => line.number).join(', ')}. Допустимы 1–32 латинские буквы, цифры или _, с необязательным @ в начале. Список не отправлен.`);
      return;
    }
    const usernames = [...new Set(normalized.map(line => line.value))];
    updateAccess(() => axios.post(`${endpoint}/bulk`, { usernames }), 'bulk');
  };

  const handleRemove = (user) => {
    if (pendingRef.current || loading || !loaded) return;
    const label = user.username ? `@${user.username}` : `пользователя с ID ${user.userId}`;
    if (!window.confirm(`Удалить ${label} из списка доступа? Доступ к этому боту будет отозван немедленно.`)) return;
    const path = user.userId != null ? `by-id/${encodeURIComponent(user.userId)}` : encodeURIComponent(user.username);
    updateAccess(() => axios.delete(`${endpoint}/${path}`));
  };

  const handleCopy = async () => {
    if (pendingRef.current || exportDisabled) return;
    pendingRef.current = true;
    setCopying(true);
    setCopySuccess(false);
    setManualCopy(null);
    setError('');
    const session = sessionRef.current;
    try {
      if (!navigator.clipboard?.writeText) throw new Error('Clipboard unavailable');
      await navigator.clipboard.writeText(exportText);
      if (session === sessionRef.current) setCopySuccess(true);
    } catch (err) {
      if (session !== sessionRef.current) return;
      setError('Не удалось скопировать автоматически. Скопируйте выделенный список вручную.');
      setManualCopy({ text: exportText });
    } finally {
      if (session === sessionRef.current) {
        pendingRef.current = false;
        setCopying(false);
      }
    }
  };

  const handleDownload = () => {
    if (pendingRef.current || exportDisabled) return;
    setError('');
    let objectUrl;
    let link;
    try {
      const blob = new Blob([exportText], { type: 'text/plain;charset=utf-8' });
      objectUrl = URL.createObjectURL(blob);
      const safeUsername = (bot?.username || '').replace(/[^a-zA-Z0-9_-]/g, '_').slice(0, 64) || 'bot';
      link = document.createElement('a');
      link.href = objectUrl;
      link.download = `private-bot-${safeUsername}-users.txt`;
      document.body.appendChild(link);
      link.click();
    } catch (err) {
      setError('Не удалось скачать TXT. Попробуйте скопировать список.');
    } finally {
      if (link) link.remove();
      if (objectUrl) setTimeout(() => URL.revokeObjectURL(objectUrl), 0);
    }
  };

  const handleClose = () => {
    if (!pendingRef.current) onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth aria-labelledby="private-access-title">
      <DialogTitle id="private-access-title">Доступ к боту @{bot?.username}</DialogTitle>
      <DialogContent>
        <Alert severity="info" sx={{ mb: 2 }}>
          Добавьте любой @username, даже если пользователь ещё не заходил в систему. До первого обращения к этому боту запись ожидает входа. При первом обращении сервер привяжет неизменяемый Telegram ID: смена username не передаёт доступ другому человеку. Удаление записи немедленно отзывает доступ.
        </Alert>
        <Typography variant="body2" sx={{ mb: 2 }}>
          Список действует только для этого бота. Изменения сохраняются сразу, отдельно от настроек бота.
        </Typography>
        {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}
        <Button onClick={() => { if (!pendingRef.current) setReload(value => value + 1); }} disabled={loading || busy}>
          Обновить список
        </Button>
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', my: 1 }}>
          <Button onClick={handleCopy} disabled={exportDisabled}>Скопировать список</Button>
          <Button onClick={handleDownload} disabled={exportDisabled}>Скачать TXT</Button>
        </Box>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          Копирование и TXT содержат только @username, без привязок Telegram ID. Пользователи без username не экспортируются. При импорте в другого бота потребуется первый вход для привязки ID. Если username изменился, он может принадлежать другому человеку — проверьте список перед импортом.
        </Typography>
        {copySuccess && <Alert severity="success" sx={{ mb: 2 }}>Список скопирован</Alert>}
        {manualCopy && (
          <TextField
            label="Список для ручного копирования"
            value={manualCopy.text}
            inputRef={manualCopyRef}
            InputProps={{ readOnly: true }}
            onFocus={event => event.target.select()}
            multiline
            minRows={3}
            maxRows={8}
            fullWidth
            sx={{ mb: 2 }}
          />
        )}
        {loading && (
          <Box role="status" sx={{ display: 'flex', gap: 1, alignItems: 'center', my: 2 }}>
            <CircularProgress size={20} /> Загрузка списка доступа…
          </Box>
        )}
        {!loading && loaded && allowedUsers.length === 0 && (
          <Alert severity="warning" sx={{ my: 2 }}>Список доступа пуст. Никто не может пользоваться этим приватным ботом.</Alert>
        )}
        {!loading && loaded && (
          <List aria-label="Разрешённые пользователи" sx={{ maxHeight: 320, overflowY: 'auto' }}>
            {allowedUsers.map(user => (
              <ListItem key={user.userId != null ? `id:${user.userId}` : `username:${user.username}`} disableGutters sx={{ gap: 1, flexWrap: 'wrap' }}>
                <Typography sx={{ overflowWrap: 'anywhere' }}>{user.username ? `@${user.username}` : 'Без username'}</Typography>
                <Chip
                  size="small"
                  color={user.userId == null ? 'warning' : 'success'}
                  label={user.userId == null ? 'Ожидает первого входа' : `Привязан ID: ${user.userId}`}
                />
                <Button
                  color="error"
                  size="small"
                  sx={{ ml: 'auto' }}
                  aria-label={user.username ? `Удалить @${user.username}` : `Удалить пользователя с ID ${user.userId}`}
                  disabled={busy}
                  onClick={() => handleRemove(user)}
                >
                  Удалить
                </Button>
              </ListItem>
            ))}
          </List>
        )}
        <Box component="form" aria-label="Добавить пользователя" onSubmit={handleAdd} noValidate sx={{ mt: 2 }}>
          <TextField
            label="Username пользователя"
            placeholder="@username"
            value={username}
            onChange={event => setUsername(event.target.value)}
            disabled={loading || busy || !loaded}
            fullWidth
            helperText="1–32 латинские буквы, цифры или _. Регистр не важен, @ необязателен."
          />
          <Button type="submit" variant="contained" disabled={loading || busy || !loaded || !username.trim()} sx={{ mt: 2 }}>
            Добавить
          </Button>
        </Box>
        <Box component="form" aria-label="Массовое добавление пользователей" onSubmit={handleBulkAdd} noValidate sx={{ mt: 3 }}>
          <TextField
            label="Пользователи списком"
            value={bulkText}
            onChange={event => setBulkText(event.target.value)}
            disabled={loading || busy || !loaded}
            multiline
            minRows={4}
            maxRows={10}
            fullWidth
            helperText="Один @username на строку, до 1000 непустых строк. Пустые строки и повторы игнорируются. Уже добавленные пользователи сохранят свои привязки ID."
          />
          <Button type="submit" variant="contained" disabled={loading || busy || !loaded || !bulkText.trim()} sx={{ mt: 2 }}>
            Добавить списком
          </Button>
        </Box>
        {saving && <Typography role="status" sx={{ mt: 2 }}>Сохранение изменений…</Typography>}
        {copying && <Typography role="status" sx={{ mt: 2 }}>Копирование списка…</Typography>}
      </DialogContent>
      <DialogActions>
        <Button onClick={handleClose} disabled={busy}>Закрыть</Button>
      </DialogActions>
    </Dialog>
  );
}
